import LabelData from '../../ui/LabelData';
import { BasilEyeClosedOutline, IconamoonEyeLight } from '../../../assets/icons/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { resetClientForm, selectCurrentClientId, selectedIsGenerateNewPINOrDelete, selectedIsShowPin, setIsBooleanType, setIsGenerateNewPINOrDelete, setQrCode } from '../../../features/client/clientSlice';
import { formatScheduleRange } from '../../../utils/formatSchedule';
import { selectCurrentAdditionalModalType, setTypeOfAdditionalModal } from '../../../features/modal/modalSlice';
import { useGetClientByIdQuery } from '../../../features/client/clientApiSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import Loading from '../../ui/Loading';
import ErrorMessage from '../../ui/ErrorMessage';
import { selectCurrentUserRole } from '../../../features/auth/authSlice';
import { formatTime } from '../../../utils/formatDateAndTime';
import { formatChapels } from '../../../utils/chapelsFormater';
import { isAdmin, isDataAdmin, isSuperAdmin } from '../../../utils/roleUtils';
import { useScheduleExpired, useScheduleExpiredButHasUpdate } from '../../../utils/isExpiredClient';
import ConfirmationModal from '../ConfirmationModal';
import useClientAction from '../../../hooks/actions/useClientAction';
import ClientDetailsModalHeader from '../../common/headers/ClientDetailsModalHeader';
import ExportBillingTypeModal from '../Admin/ExportBillingTypeModal';
import { setSelectedClientIdBillingReport, setSelectedDeceaseName } from '../../../features/reports/reportsSlice';

interface ClientDetailsModalProps {
    setOpenQRCodeModal: (value: boolean) => void
}

export default function ClientDetailsModal({ setOpenQRCodeModal }: ClientDetailsModalProps) {
    
    const typeAdditionalModal = useSelector(selectCurrentAdditionalModalType);
    const dispatch = useDispatch();
    const clientId = useSelector(selectCurrentClientId) ?? null;
    const showPin = useSelector(selectedIsShowPin);
    const adminRole = useSelector(selectCurrentUserRole)
    const typeOfAction = useSelector(selectedIsGenerateNewPINOrDelete);
    const { handleCancelGeneratePinOrDeleteClient, handleGeneratePinOrDeleteClient, isLoadingDeleteClient, isLoadingGenerateNewPIN } = useClientAction()
    const { data: clientInfo, isLoading: isLoadingClientInfo, isError: isErrorClientInfo } = useGetClientByIdQuery(clientId !== null ? clientId : skipToken);
    const hasDeletePermission = isSuperAdmin(adminRole) || isAdmin(adminRole)
    const hasUpdateBtnDisplay = isDataAdmin(adminRole) || isSuperAdmin(adminRole) || isAdmin(adminRole)
    const hasGeneratePINBtnDisplay = isDataAdmin(adminRole) || isSuperAdmin(adminRole) || isAdmin(adminRole)
    const isScheduleExpired = useScheduleExpired(clientInfo?.schedule_to);
    const isScheduleExpiredButHasUpdate = useScheduleExpiredButHasUpdate(clientInfo?.schedule_to);

    const scheduledTime = clientInfo?.schedule_from && clientInfo?.schedule_to ? `${formatTime(clientInfo.schedule_from)} - ${formatTime(clientInfo.schedule_to)}` : ''
    const scheduledData = clientInfo?.schedule_from && clientInfo?.schedule_to ? formatScheduleRange(clientInfo.schedule_from, clientInfo.schedule_to, 'utc') : ''
    const chapels = clientInfo?.chapels && formatChapels(clientInfo?.chapels)
    const hasContactPerson = clientInfo?.contacts.length !== 0
    const contactPerson = clientInfo?.contacts ?? []
    const modalTitle = typeOfAction === 'deleteClient' ? 'Delete Client'
        : typeOfAction === 'generatePIN' ? 'Generate New PIN'
        : '';
    const modalSubTitle = typeOfAction === 'deleteClient' ? 'Are you sure you want to delete this client?'
        : typeOfAction === 'generatePIN' ? 'Are you sure you want to generate a new PIN for this client?'
        : '';

    return (
        <div className='fixed m-0 p-0 inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex justify-center items-center z-[100]'>
            <div className='relative w-[90vw] mx-5 bg-white rounded-lg shadow px-5 flex md:w-[75vw] lg:w-[70vw] xl:w-[70vw]'>
                <div className='flex flex-col w-full'>
                    <div className='py-5'>
                        <ClientDetailsModalHeader/>
                        <hr className="h-px bg-gray-200 border-0" />
                        <div className='pt-3 space-y-2'>
                            {isLoadingClientInfo ? (
                                <Loading message='Loading'/>
                            ) : isErrorClientInfo ? (
                                <ErrorMessage message='Something went wrong. Please try again.'/>
                            ) : (
                                <>
                                    <div className='w-full'>
                                        <div className='grid grid-cols-2 gap-2'>
                                            <p className='text-sm font-medium pb-1'>Client Details:</p>
                                        </div>
                                        <div className='grid grid-cols-3 gap-3'>
                                            <LabelData
                                                labelName={'Deceased Name'}
                                                labelValue={clientInfo?.deceasedName || ''}
                                            />
                                            <LabelData 
                                                labelName={'Client Name'}
                                                labelValue={clientInfo?.registeredBy || ''}
                                            />
                                            <div className='flex flex-col'>
                                                <p className='text-sm'>Chapel:</p>
                                                <p className='text-[15px] font-medium break-words'>
                                                    {chapels}
                                                </p>
                                            </div>
                                            <LabelData 
                                                labelName={'Scheduled'}
                                                labelValue={scheduledData}
                                                addValue={scheduledTime}
                                            />
                                            <div className="flex flex-col">
                                                <p className="text-sm">PIN:</p>
                                                <div className="relative">
                                                    <input
                                                        type={showPin ? "text" : "password"}
                                                        value={clientInfo?.pin?.toString() || ""}
                                                        readOnly
                                                        className="w-full pr-10 text-[15px] font-medium bg-transparent py-1 focus:outline-none"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => dispatch(setIsBooleanType({type: 'showPin', value: !showPin}))}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-500"
                                                    >
                                                        {showPin ? (
                                                            <IconamoonEyeLight width={18} height={18} />
                                                        ) : (
                                                            <BasilEyeClosedOutline width={18} height={18} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    { hasContactPerson && (
                                        <div className=''>
                                            <p className='text-sm font-medium pb-1'>Contact Person:</p>
                                            <div className='grid grid-cols-2 gap-3'>
                                                    <p className='text-sm'>{'Name'}:</p>
                                                    <p className='text-sm'>{'Number'}:</p>
                                            </div>
                                            {contactPerson.map((contact) => (
                                                <div key={contact.contactID} className="grid grid-cols-2 gap-3">
                                                    <p className='text-[15px] font-medium break-words'>{contact.contactPersonName}</p>
                                                    <p className='text-[15px] font-medium break-words'>{contact.contactPersonNumber}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className='w-full'>
                                        <div className='grid grid-cols-2 gap-2'>
                                            <p className='text-sm font-medium pb-1'>Heritage Details:</p>
                                        </div>
                                        <div className='grid grid-cols-2 gap-3'>
                                            <LabelData 
                                                labelName={'Heritage Mobile Number'}
                                                labelValue={clientInfo?.mobileNo?.toString() || ''}
                                            />
                                            <LabelData 
                                                labelName={'Heritage Email Address'}
                                                labelValue={clientInfo?.email || ''}
                                            />
                                        </div>
                                    </div>
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3 pt-2'>
                                        { hasDeletePermission ? (
                                            <div className='grid grid-cols-2 gap-3'>
                                                <button
                                                    onClick={() => {
                                                        if (!clientInfo?.qrCodeBase64) return;
                                                        dispatch(setQrCode(clientInfo?.qrCodeBase64));
                                                        setOpenQRCodeModal(true)
                                                    }} 
                                                    className='flex-1 text-sm px-2 py-2 rounded font-medium transition-all bg-cbColor text-white shadow-md'
                                                >
                                                    View QR Code
                                                </button>
                                                <button 
                                                    onClick={() => {
                                                        dispatch(setTypeOfAdditionalModal('viewClientPackages'))
                                                    }}
                                                    className='flex-1 text-sm px-2 py-2 rounded font-medium transition-all bg-cbColor text-white shadow-md'
                                                >
                                                    View Package
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        if (!clientInfo?.qrCodeBase64) return;
                                                        dispatch(setQrCode(clientInfo?.qrCodeBase64));
                                                        setOpenQRCodeModal(true)
                                                    }} 
                                                    className='flex-1 text-sm px-2 py-2 rounded font-medium transition-all bg-cbColor text-white shadow-md'
                                                >
                                                    View QR Code
                                                </button>
                                                <button 
                                                    onClick={() => {
                                                        dispatch(setTypeOfAdditionalModal('viewClientPackages'))
                                                    }}
                                                    className='flex-1 text-sm px-4 py-2 rounded font-medium transition-all bg-cbColor text-white shadow-md'
                                                >
                                                    View Package
                                                </button>
                                            </>
                                        )}
                                        
                                        <div className='grid grid-cols-2 gap-3'>
                                            {hasGeneratePINBtnDisplay && (
                                                <button 
                                                    onClick={() => {
                                                        dispatch(setTypeOfAdditionalModal('showDeleteModal'))
                                                        dispatch(setIsGenerateNewPINOrDelete('generatePIN'))
                                                    }}
                                                    // disabled={isScheduleExpired}
                                                    className={`flex-1 text-sm px-4 py-2 rounded font-medium transition-all bg-cbColor shadow-md text-white disabled:cursor-not-allowed`}
                                                >
                                                    Generate PIN
                                                </button> 
                                            )}
                                            {hasGeneratePINBtnDisplay && (
                                                <button 
                                                    onClick={() => {
                                                        if(!clientInfo?.deceasedName || !clientInfo.clientID) return
                                                        dispatch(setSelectedClientIdBillingReport(clientInfo.clientID))
                                                        dispatch(setSelectedDeceaseName(clientInfo.deceasedName))
                                                        dispatch(setTypeOfAdditionalModal('showExportBillingTypes'))
                                                    }}
                                                    className={`flex-1 text-sm px-4 py-2 rounded font-medium transition-all bg-cbColor shadow-md text-white disabled:cursor-not-allowed`}
                                                >
                                                    Export Billing
                                                </button> 
                                            )}
                                        </div>
                                        {hasUpdateBtnDisplay && (
                                            <button 
                                                onClick={() => {
                                                    dispatch(resetClientForm())
                                                    dispatch(setTypeOfAdditionalModal('updateClient'))
                                                }}
                                                disabled={isScheduleExpiredButHasUpdate}
                                                className={`flex-1 text-sm px-4 py-2 rounded font-medium transition-all bg-cbColor shadow-md  disabled:cursor-not-allowed text-white`}
                                            >
                                                Update
                                            </button>
                                        )}
                                        
                                        {hasDeletePermission && (
                                            <button
                                                onClick={() => {
                                                    dispatch(setTypeOfAdditionalModal('showDeleteModal'))
                                                    dispatch(setIsGenerateNewPINOrDelete('deleteClient'))
                                                }}
                                                // disabled={isScheduleExpired}
                                                className={`flex-1 text-sm px-4 py-2 rounded font-medium transition-all bg-red-600 shadow-md text-white disabled:cursor-not-allowed`}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {typeAdditionalModal === 'showExportBillingTypes' && (<ExportBillingTypeModal/>)}
            {typeAdditionalModal === 'showDeleteModal' && typeOfAction && (
                <ConfirmationModal
                    modalSubTitle={modalSubTitle}
                    modalTitle={modalTitle}
                    onCancel={handleCancelGeneratePinOrDeleteClient}
                    onConfirm={handleGeneratePinOrDeleteClient}
                    isLoading={isLoadingGenerateNewPIN || isLoadingDeleteClient}
                />
            )}
        </div>
    )
}
